import {
  Add as AddSmallIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  CloudUpload as SaveIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  Grade as GradeIcon,
  List as ListIcon,
  TextFields as TextFieldsIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import ParameterPicker from '../components/Parameters/ParameterPicker';
import { AppLang, useDirection } from '../context/DirectionContext';
import apiService from '../services/api';
import { FormTemplate, SharedParameter } from '../types/api';
import { translate as tr, translateOr, TKey } from '../i18n/translations';

// ── Types ───────────────────────────────────────────────────────────────────

export type FormType = 'generic' | 'socio' | 'interview';

export interface GradeBoxField {
  type: 'gradebox';
  title: string;
  fieldKey: string;
  sharedParameterId?: string;
  required?: boolean;
  maxGrade?: 5 | 6 | 7 | 8 | 9 | 10;
  showTextInput?: boolean;
  textFieldKey?: string;
  textPlaceholder?: string;
}

export interface TextBoxField {
  type: 'textboxblock';
  title: string;
  fieldKey: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
}

export interface FlagField {
  type: 'flagblock';
  title: string;
  fieldKey: string;
  flagged?: boolean;
  description?: string;
  textFieldKey?: string;
  textPlaceholder?: string;
}

export interface SelectBoxField {
  type: 'selectbox';
  title: string;
  fieldKey: string;
  required?: boolean;
  options: string[];
  multiple?: boolean;
}

export interface SocioBox {
  id: string;
  label: string;
  color: string;
  score: number;
}

export type FormField = GradeBoxField | TextBoxField | FlagField | SelectBoxField;

export interface FormStructure {
  title: string;
  description: string;
  icon: string;
  isSortable?: boolean;
  adjustable?: boolean;
  submitRanking?: boolean;
  showByName?: boolean;
  singleSubmission?: boolean;
  structure: FormField[];
  boxes?: SocioBox[];
}

export interface FormDefinitionDraft {
  name: string;
  formType: FormType;
  roleRelevancy: string[];
  weightInFinalScore: number;
  isMandatoryForClosure: boolean;
  isActive: boolean;
  singleSubmission?: boolean;
  structure: FormStructure;
}

// ── Templates ────────────────────────────────────────────────────────────────

const TEMPLATES: Record<string, FormDefinitionDraft> = {
  generic: {
    name: 'Assessment Form',
    formType: 'generic',
    roleRelevancy: ['assessor'],
    weightInFinalScore: 1.0,
    isMandatoryForClosure: true,
    isActive: true,
    structure: {
      title: 'הערכה כללית',
      description: 'טופס הערכה כללי למועמד',
      icon: '📋',
      structure: [
        {
          type: 'gradebox',
          title: 'ציון כולל',
          fieldKey: 'overallGrade',
          required: true,
          maxGrade: 7,
          showTextInput: true,
          textFieldKey: 'overallComment',
          textPlaceholder: 'הכנס הערותיך כאן...',
        },
        {
          type: 'textboxblock',
          title: 'הערות כלליות',
          fieldKey: 'generalNotes',
          required: false,
          placeholder: 'הכנס הערות נוספות...',
          maxLength: 500,
        },
        {
          type: 'flagblock',
          title: 'דגל אדום',
          fieldKey: 'redFlag',
          flagged: true,
          description: 'סמן אם קיים חשש',
          textFieldKey: 'redFlagNote',
          textPlaceholder: 'פרט...',
        },
      ],
    },
  },
  interview: {
    name: 'Interview Form',
    formType: 'interview',
    roleRelevancy: ['interviewer'],
    weightInFinalScore: 1.0,
    isMandatoryForClosure: true,
    isActive: true,
    structure: {
      title: 'ראיון אישי',
      description: 'טופס הערכת ראיון אישי',
      icon: '🎯',
      adjustable: true,
      structure: [
        {
          type: 'gradebox',
          title: 'רושם כללי',
          fieldKey: 'overallImpression',
          required: true,
          maxGrade: 7,
        },
        {
          type: 'gradebox',
          title: 'כישורים מקצועיים',
          fieldKey: 'professionalSkills',
          required: true,
          maxGrade: 7,
        },
        {
          type: 'gradebox',
          title: 'כישורים בינאישיים',
          fieldKey: 'interpersonalSkills',
          required: true,
          maxGrade: 7,
        },
        {
          type: 'textboxblock',
          title: 'סיכום ראיון',
          fieldKey: 'interviewSummary',
          required: true,
          placeholder: 'סכם את הראיון...',
          maxLength: 1000,
        },
      ],
    },
  },
  socio: {
    name: 'Sociometric Form',
    formType: 'socio',
    roleRelevancy: ['socio'],
    weightInFinalScore: 0.0,
    isMandatoryForClosure: false,
    isActive: true,
    structure: {
      title: 'שאלון סוציומטרי',
      description: 'הערכת יחסים חברתיים',
      icon: '🤝',
      structure: [],
      boxes: [
        { id: 'box_1', label: 'קטגוריה 1', color: '#10b981', score: 100 },
        { id: 'box_2', label: 'קטגוריה 2', color: '#3b82f6', score: 75 },
        { id: 'box_3', label: 'קטגוריה 3', color: '#f59e0b', score: 50 },
        { id: 'box_4', label: 'קטגוריה 4', color: '#ef4444', score: 25 },
      ],
    },
  },
};

// ── Utilities ────────────────────────────────────────────────────────────────

function cleanObject(obj: any): any {
  if (Array.isArray(obj)) return obj.map(cleanObject);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== '' && v !== null)
        .map(([k, v]) => [k, cleanObject(v)])
    );
  }
  return obj;
}

const rand = () => Math.random().toString(36).slice(2, 7);

export function buildFormStructure(form: FormDefinitionDraft, bank?: SharedParameter[]): Record<string, unknown> {
  const base: any = {};
  if (form.structure.title)       base.title       = form.structure.title;
  if (form.structure.description) base.description = form.structure.description;
  if (form.structure.icon)        base.icon        = form.structure.icon;
  if (form.formType === 'socio') {
    if (form.structure.boxes?.length) base.boxes = form.structure.boxes;
  } else {
    const flags = ['isSortable', 'adjustable', 'submitRanking', 'showByName', 'singleSubmission'] as const;
    for (const f of flags) if ((form.structure as any)[f]) base[f] = true;
    if (form.structure.structure?.length) {
      base.structure = form.structure.structure.map((field) => {
        if (field.type === 'gradebox') {
          const bp = bank?.find((p) => p.id === field.sharedParameterId);
          return {
            ...field,
            title: bp?.name ?? field.title,
            maxGrade: (bp?.maxGrade ?? field.maxGrade) as GradeBoxField['maxGrade'],
            sharedParameterId: field.sharedParameterId || undefined,
          };
        }
        return field;
      });
    }
  }
  return base;
}

export function buildFormOutput(form: FormDefinitionDraft, bank?: SharedParameter[]): any {
  return cleanObject({
    name: form.name,
    formType: form.formType,
    roleRelevancy: form.roleRelevancy,
    weightInFinalScore: form.weightInFinalScore,
    isMandatoryForClosure: form.isMandatoryForClosure,
    isActive: form.isActive,
    structure: buildFormStructure(form, bank),
  });
}

/** One message per gradebox not linked to the bank; saving is blocked while non-empty. */
export function validateFormParameterLinks(
  input: FormDefinitionDraft | FormStructure | FormField[],
  lang: AppLang = 'en',
): string[] {
  let fields: FormField[] = [];
  if (Array.isArray(input)) {
    fields = input;
  } else if ('structure' in input) {
    if (Array.isArray(input.structure)) {
      fields = input.structure;
    } else if (input.structure && Array.isArray((input.structure as any).structure)) {
      fields = (input.structure as any).structure;
    }
  }

  return fields.flatMap((f, i) =>
    f.type === 'gradebox' && !f.sharedParameterId
      ? [
          tr('param.unlinkedGradeBox', lang)
            .replace('{n}', String(i + 1))
            .replace('{name}', f.title || tr('param.unnamed', lang)),
        ]
      : [],
  );
}

const defaultForm = (): FormDefinitionDraft => ({
  name: '',
  formType: 'generic',
  roleRelevancy: ['assessor'],
  weightInFinalScore: 1.0,
  isMandatoryForClosure: true,
  isActive: true,
  structure: {
    title: '',
    description: '',
    icon: '📋',
    structure: [],
  },
});

const ALL_ROLES = ['admin', 'teamlead', 'assessor', 'socio', 'interviewer', 'viewer'];

const FORM_TYPE_COLOR: Record<string, string> = {
  generic: '#10B981',
  interview: '#6366F1',
  socio: '#0EA5E9',
};

// ── Color palette ─────────────────────────────────────────────────────────────

const COLOR_PALETTE = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#0ea5e9', '#f97316', '#ec4899', '#14b8a6', '#6366f1',
  '#84cc16', '#6b7280',
];

const ColorPicker: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => {
  const { lang } = useDirection();
  return (
  <Box>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, mb: 0.75 }}>{tr('fb.color', lang)}</Typography>
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {COLOR_PALETTE.map(c => (
        <Box
          key={c}
          onClick={() => onChange(c)}
          sx={{
            width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
            boxShadow: value === c ? `0 0 0 2px white, 0 0 0 4px ${c}` : 'none',
            transition: 'transform 0.1s',
            '&:hover': { transform: 'scale(1.2)' },
          }}
        />
      ))}
    </Box>
  </Box>
);
};

// ── Field editors ─────────────────────────────────────────────────────────────

const halfSx = { flex: 1, mb: 2 };
const fullSx = { mb: 2 };

const GradeBoxEditor: React.FC<{
  field: GradeBoxField;
  options: SharedParameter[];
  lang: AppLang;
  onChange: (f: GradeBoxField) => void;
}> = ({ field, options, lang, onChange }) => {
  return (
  <Box>
    <Box sx={{ mb: 2 }}>
      <ParameterPicker
        value={field.sharedParameterId ?? null}
        options={options}
        legacyName={field.sharedParameterId ? undefined : field.title || undefined}
        onChange={(p) =>
          onChange(
            p
              ? {
                  ...field,
                  sharedParameterId: p.id,
                  title: p.name,
                  maxGrade: p.maxGrade as GradeBoxField['maxGrade'],
                }
              : {
                  ...field,
                  sharedParameterId: undefined,
                },
          )
        }
      />
    </Box>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography data-testid="scale-readonly" sx={{ fontSize: '0.8rem', color: '#475569', mb: 2 }}>
        {tr('param.scale', lang)}: <span dir="ltr">{field.sharedParameterId && field.maxGrade ? `1–${field.maxGrade}` : '—'}</span>
      </Typography>
      <FormControlLabel control={<Switch checked={!!field.required} onChange={(e) => onChange({ ...field, required: e.target.checked })} size="small" />} label={tr('sb.required', lang)} sx={{ mb: 2 }} />
      <FormControlLabel control={<Switch checked={!!field.showTextInput} onChange={(e) => onChange({ ...field, showTextInput: e.target.checked })} size="small" />} label={tr('fb.textInput', lang)} sx={{ mb: 2 }} />
    </Box>
    {field.showTextInput && (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField label={tr('fb.textPlaceholder', lang)} size="small" value={field.textPlaceholder ?? ''} onChange={(e) => onChange({ ...field, textPlaceholder: e.target.value })} sx={halfSx} />
      </Box>
    )}
  </Box>
);
};

const TextBoxEditor: React.FC<{ field: TextBoxField; onChange: (f: TextBoxField) => void }> = ({ field, onChange }) => {
  const { lang } = useDirection();
  return (
  <Box>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField label={tr('fb.title', lang)} size="small" value={field.title} onChange={e => onChange({ ...field, title: e.target.value })} sx={halfSx} />
      <TextField label={tr('fb.placeholder', lang)} size="small" value={field.placeholder ?? ''} onChange={e => onChange({ ...field, placeholder: e.target.value })} sx={halfSx} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField label={tr('fb.maxLength', lang)} size="small" type="number" value={field.maxLength ?? ''} onChange={e => onChange({ ...field, maxLength: e.target.value ? Number(e.target.value) : undefined })} sx={halfSx} />
      <Box sx={halfSx} />
    </Box>
    <FormControlLabel control={<Switch checked={!!field.required} onChange={e => onChange({ ...field, required: e.target.checked })} size="small" />} label={tr('sb.required', lang)} sx={{ mb: 1 }} />
  </Box>
);
};

const FlagEditor: React.FC<{ field: FlagField; onChange: (f: FlagField) => void }> = ({ field, onChange }) => {
  const { lang } = useDirection();
  return (
  <Box>
    <TextField label={tr('fb.title', lang)} size="small" value={field.title} onChange={e => onChange({ ...field, title: e.target.value })} sx={fullSx} fullWidth />
    <TextField label={tr('fb.description', lang)} size="small" value={field.description ?? ''} onChange={e => onChange({ ...field, description: e.target.value })} sx={fullSx} fullWidth />
    <TextField label={tr('fb.textPlaceholder', lang)} size="small" value={field.textPlaceholder ?? ''} onChange={e => onChange({ ...field, textPlaceholder: e.target.value })} sx={fullSx} fullWidth />
    <FormControlLabel control={<Switch checked={!!field.flagged} onChange={e => onChange({ ...field, flagged: e.target.checked })} size="small" />} label={tr('fb.showFlag', lang)} sx={{ mb: 1 }} />
  </Box>
);
};

const SelectBoxEditor: React.FC<{ field: SelectBoxField; onChange: (f: SelectBoxField) => void }> = ({ field, onChange }) => {
  const { lang } = useDirection();
  const [newOption, setNewOption] = useState('');
  return (
    <Box>
      <TextField label={tr('fb.title', lang)} size="small" value={field.title} onChange={e => onChange({ ...field, title: e.target.value })} sx={fullSx} fullWidth />
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControlLabel control={<Switch checked={!!field.required} onChange={e => onChange({ ...field, required: e.target.checked })} size="small" />} label={tr('sb.required', lang)} sx={{ mb: 2 }} />
        <FormControlLabel control={<Switch checked={!!field.multiple} onChange={e => onChange({ ...field, multiple: e.target.checked })} size="small" />} label={tr('fb.multiSelect', lang)} sx={{ mb: 2 }} />
      </Box>
      <Box sx={{ mb: 1 }}>
        {(field.options ?? []).map((opt, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip label={opt} size="small" onDelete={() => onChange({ ...field, options: field.options.filter((_, j) => j !== i) })} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" placeholder={tr('fb.addOption', lang)} value={newOption} onChange={e => setNewOption(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newOption.trim()) { onChange({ ...field, options: [...(field.options ?? []), newOption.trim()] }); setNewOption(''); e.preventDefault(); } }}
          sx={{ flex: 1 }}
        />
        <IconButton size="small" onClick={() => { if (newOption.trim()) { onChange({ ...field, options: [...(field.options ?? []), newOption.trim()] }); setNewOption(''); } }} sx={{ color: '#6366F1' }}>
          <AddSmallIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ── FieldBlock card ───────────────────────────────────────────────────────────

const FIELD_META = {
  gradebox:    { labelKey: 'fb.field.gradebox',     color: '#6366F1', Icon: GradeIcon },
  textboxblock:{ labelKey: 'fb.field.textboxblock', color: '#10B981', Icon: TextFieldsIcon },
  flagblock:   { labelKey: 'fb.field.flagblock',    color: '#F59E0B', Icon: FlagIcon },
  selectbox:   { labelKey: 'fb.field.selectbox',    color: '#8B5CF6', Icon: ListIcon },
};

const FieldBlock: React.FC<{
  index: number;
  total: number;
  field: FormField;
  options: SharedParameter[];
  lang: AppLang;
  onChange: (f: FormField) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}> = ({ index, total, field, options, lang, onChange, onRemove, onMove }) => {
  const { labelKey, color, Icon } = FIELD_META[field.type];
  const label = tr(labelKey as TKey, lang);
  return (
    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: `3px solid ${color}`, borderRadius: 2, p: 2, mb: 2, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 15, color }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0F172A' }}>{label}</Typography>
          <Chip label={`#${index + 1}`} size="small" sx={{ height: 17, fontSize: '0.63rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <IconButton size="small" onClick={() => onMove('up')} disabled={index === 0} sx={{ color: '#94A3B8' }}><ArrowUpIcon sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={() => onMove('down')} disabled={index === total - 1} sx={{ color: '#94A3B8' }}><ArrowDownIcon sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={onRemove} sx={{ color: '#F87171', '&:hover': { bgcolor: '#FEF2F2' } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      </Box>
      {field.type === 'gradebox'     && <GradeBoxEditor   field={field} options={options} lang={lang} onChange={f => onChange(f)} />}
      {field.type === 'textboxblock' && <TextBoxEditor    field={field} onChange={f => onChange(f)} />}
      {field.type === 'flagblock'    && <FlagEditor       field={field} onChange={f => onChange(f)} />}
      {field.type === 'selectbox'    && <SelectBoxEditor  field={field} onChange={f => onChange(f)} />}
    </Paper>
  );
};

// ── Section title helper ──────────────────────────────────────────────────────

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', mb: 1.5 }}>
    {children}
  </Typography>
);

// ── SocioBoxBlock card ────────────────────────────────────────────────────────

const SocioBoxBlock: React.FC<{
  index: number;
  total: number;
  box: SocioBox;
  onChange: (b: SocioBox) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}> = ({ index, total, box, onChange, onRemove, onMove }) => {
  const { lang } = useDirection();
  return (
  <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: `3px solid ${box.color || '#0EA5E9'}`, borderRadius: 2, p: 2, mb: 2, bgcolor: 'white' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0F172A' }}>{tr('fb.box', lang)}</Typography>
        <Chip label={`#${index + 1}`} size="small" sx={{ height: 17, fontSize: '0.63rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <IconButton size="small" onClick={() => onMove('up')} disabled={index === 0} sx={{ color: '#94A3B8' }}><ArrowUpIcon sx={{ fontSize: 15 }} /></IconButton>
        <IconButton size="small" onClick={() => onMove('down')} disabled={index === total - 1} sx={{ color: '#94A3B8' }}><ArrowDownIcon sx={{ fontSize: 15 }} /></IconButton>
        <IconButton size="small" onClick={onRemove} sx={{ color: '#F87171', '&:hover': { bgcolor: '#FEF2F2' } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField label={tr('fb.label', lang)} size="small" value={box.label} onChange={e => onChange({ ...box, label: e.target.value })} sx={{ flex: 1, mb: 2 }} placeholder={tr('fb.egLabel', lang)} />
      <TextField label={tr('fb.score', lang)} size="small" type="number" value={box.score} onChange={e => onChange({ ...box, score: Number(e.target.value) })} inputProps={{ min: 0, max: 100 }} sx={{ width: 90, mb: 2 }} />
    </Box>
    <ColorPicker value={box.color} onChange={c => onChange({ ...box, color: c })} />
  </Paper>
);
};

// ── Main FormBuilder ──────────────────────────────────────────────────────────

const FormBuilder: React.FC = () => {
  const { lang } = useDirection();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState<FormDefinitionDraft>(defaultForm());
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<FormTemplate | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: bankParams = [] } = useQuery<SharedParameter[]>({
    queryKey: ['parameters'],
    queryFn: () => apiService.getParameters(),
  });

  const linkErrors = validateFormParameterLinks(form, lang);

  const jsonStr = JSON.stringify(cleanObject({
    name: form.name,
    formType: form.formType,
    roleRelevancy: form.roleRelevancy,
    weightInFinalScore: form.weightInFinalScore,
    isMandatoryForClosure: form.isMandatoryForClosure,
    isActive: form.isActive,
    structure: buildFormStructure(form, bankParams),
  }), null, 2);

  const updateMeta = (patch: Partial<FormDefinitionDraft>) => setForm(f => ({ ...f, ...patch }));
  const updateStructMeta = (patch: Partial<FormStructure>) => setForm(f => ({ ...f, structure: { ...f.structure, ...patch } }));

  const updateField = (i: number, field: FormField) =>
    setForm(f => { const s = [...f.structure.structure]; s[i] = field; return { ...f, structure: { ...f.structure, structure: s } }; });

  const removeField = (i: number) =>
    setForm(f => ({ ...f, structure: { ...f.structure, structure: f.structure.structure.filter((_, idx) => idx !== i) } }));

  const moveField = (i: number, dir: 'up' | 'down') =>
    setForm(f => {
      const s = [...f.structure.structure];
      const t = dir === 'up' ? i - 1 : i + 1;
      [s[i], s[t]] = [s[t], s[i]];
      return { ...f, structure: { ...f.structure, structure: s } };
    });

  const addField = (type: FormField['type']) => {
    const key = `${type === 'textboxblock' ? 'text' : type === 'gradebox' ? 'grade' : type === 'flagblock' ? 'flag' : 'select'}_${rand()}`;
    const blank: FormField =
      type === 'gradebox'     ? { type: 'gradebox',     title: '', fieldKey: key, required: false, showTextInput: false, textFieldKey: `${key}_note` } :
      type === 'textboxblock' ? { type: 'textboxblock', title: '', fieldKey: key, required: false } :
      type === 'selectbox'    ? { type: 'selectbox',    title: '', fieldKey: key, required: false, options: [] } :
                                 { type: 'flagblock',   title: '', fieldKey: key, textFieldKey: `${key}_note` };
    setForm(f => ({ ...f, structure: { ...f.structure, structure: [...f.structure.structure, blank] } }));
  };

  const addSocioBox = () =>
    setForm(f => ({
      ...f,
      structure: {
        ...f.structure,
        boxes: [...(f.structure.boxes ?? []), { id: `box_${rand()}`, label: '', color: '#6366F1', score: 50 }],
      },
    }));

  const updateSocioBox = (i: number, box: SocioBox) =>
    setForm(f => ({
      ...f,
      structure: { ...f.structure, boxes: (f.structure.boxes ?? []).map((b, idx) => idx === i ? box : b) },
    }));

  const removeSocioBox = (i: number) =>
    setForm(f => ({
      ...f,
      structure: { ...f.structure, boxes: (f.structure.boxes ?? []).filter((_, idx) => idx !== i) },
    }));

  const moveSocioBox = (i: number, dir: 'up' | 'down') => {
    setForm(f => {
      const arr = [...(f.structure.boxes ?? [])];
      const t = dir === 'up' ? i - 1 : i + 1;
      [arr[i], arr[t]] = [arr[t], arr[i]];
      return { ...f, structure: { ...f.structure, boxes: arr } };
    });
  };

  const handleFormTypeChange = (newType: FormType) => {
    if (newType === 'socio' && form.formType !== 'socio') {
      setForm(f => ({
        ...f,
        formType: newType,
        structure: {
          ...f.structure,
          structure: [],
          boxes: [
            { id: `box_${rand()}`, label: '', color: '#10b981', score: 100 },
            { id: `box_${rand()}`, label: '', color: '#3b82f6', score: 75 },
            { id: `box_${rand()}`, label: '', color: '#f59e0b', score: 50 },
            { id: `box_${rand()}`, label: '', color: '#ef4444', score: 25 },
          ],
        },
      }));
    } else if (newType !== 'socio' && form.formType === 'socio') {
      setForm(f => ({ ...f, formType: newType, structure: { ...f.structure, boxes: undefined } }));
    } else {
      updateMeta({ formType: newType });
    }
  };

  const applyTemplate = (key: string) => setForm(JSON.parse(JSON.stringify(TEMPLATES[key])));

  const copyJson = async () => {
    await navigator.clipboard.writeText(jsonStr);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const downloadJson = () => {
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.name || 'form-definition'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try { setForm(JSON.parse(ev.target?.result as string)); setLoadError(''); }
      catch { setLoadError(tr('sb.invalidJson', lang)); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const toggleRole = (role: string) =>
    setForm(f => ({
      ...f,
      roleRelevancy: f.roleRelevancy.includes(role)
        ? f.roleRelevancy.filter(r => r !== role)
        : [...f.roleRelevancy, role],
    }));

  // ── Bank query ──────────────────────────────────────────────────────────────

  const { data: bankForms = [], isLoading: bankLoading } = useQuery<FormTemplate[]>({
    queryKey: ['formTemplates'],
    queryFn: () => apiService.getFormTemplates(),
    enabled: activeTab === 1,
  });

  // ── Save mutation ───────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (payload: { id: string | null; data: any }) =>
      payload.id
        ? apiService.updateFormTemplate(payload.id, payload.data)
        : apiService.createFormTemplate(payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formTemplates'] });
      setSaveMsg(tr('fb.saved', lang));
      setSaveError('');
      setForm(defaultForm());
      setEditingFormId(null);
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.message ?? tr('sb.saveFailed', lang));
      setSaveMsg('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteFormTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formTemplates'] });
      if (editingFormId === deleteTarget?.id) {
        setEditingFormId(null);
        setForm(defaultForm());
      }
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.message ?? tr('sb.deleteFailed', lang));
      setDeleteTarget(null);
    },
  });

  const handleSaveToBank = () => {
    if (!form.name.trim()) { setSaveError(tr('fb.nameRequired', lang)); return; }
    if (linkErrors.length > 0) return;
    setSaveError('');
    saveMutation.mutate({
      id: editingFormId,
      data: {
        name: form.name,
        formType: form.formType,
        roleRelevancy: form.roleRelevancy,
        isActive: form.isActive,
        structure: buildFormStructure(form, bankParams),
      },
    });
  };

  const handleEditBankForm = (bankForm: FormTemplate) => {
    setForm({
      name: bankForm.name,
      formType: bankForm.formType as FormType,
      roleRelevancy: bankForm.roleRelevancy,
      weightInFinalScore: form.weightInFinalScore,
      isMandatoryForClosure: form.isMandatoryForClosure,
      isActive: bankForm.isActive,
      structure: (bankForm.structure as FormStructure) ?? defaultForm().structure,
    });
    setEditingFormId(bankForm.id);
    setSaveMsg('');
    setSaveError('');
    setActiveTab(0);
  };

  const handleNewForm = () => {
    setForm(defaultForm());
    setEditingFormId(null);
    setSaveMsg('');
    setSaveError('');
  };

  const structureFlags: [keyof FormStructure, string][] = [
    ['isSortable',       tr('fb.flag.sortable', lang)],
    ['adjustable',       tr('fb.flag.adjustable', lang)],
    ['submitRanking',    tr('fb.flag.submitRanking', lang)],
    ['showByName',       tr('fb.flag.showByName', lang)],
    ['singleSubmission', tr('fb.flag.singleSubmission', lang)],
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>{tr('nav.formBuilder', lang)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            {tr('fb.titlePage', lang)}
            {editingFormId && (
              <Chip label={tr('fb.editingBank', lang)} size="small" color="primary" sx={{ ml: 1.5, fontWeight: 600, fontSize: '0.7rem' }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeTab === 0 && (
              <>
                {editingFormId && (
                  <Button variant="outlined" size="small" onClick={handleNewForm} sx={{ color: '#64748B', borderColor: '#CBD5E1' }}>{tr('fb.newForm', lang)}</Button>
                )}
                <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleLoadFile} />
                <Button variant="outlined" startIcon={<UploadIcon />} size="small" onClick={() => fileInputRef.current?.click()}>{tr('sb.loadJson', lang)}</Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} size="small" onClick={downloadJson}>{tr('sb.download', lang)}</Button>
                <Button
                  variant={copySuccess ? 'contained' : 'outlined'}
                  color={copySuccess ? 'success' : 'primary'}
                  startIcon={<CopyIcon />}
                  size="small"
                  onClick={copyJson}
                >
                  {copySuccess ? tr('sb.copied', lang) : tr('sb.copyJson', lang)}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  size="small"
                  onClick={handleSaveToBank}
                  disabled={saveMutation.isPending || linkErrors.length > 0}
                  sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}
                >
                  {saveMutation.isPending ? tr('common.saving', lang) : editingFormId ? tr('sb.updateInBank', lang) : tr('sb.saveToBank', lang)}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2.5, borderBottom: '1px solid #E2E8F0', '& .MuiTab-root': { fontWeight: 600, fontSize: '0.82rem', textTransform: 'none' } }}
      >
        <Tab label={tr('sb.tab.builder', lang)} />
        <Tab label={tr('fb.tab.bank', lang)} />
      </Tabs>

      {saveMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveMsg('')}>{saveMsg}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}
      {activeTab === 0 && linkErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {linkErrors.map(m => <div key={m}>{m}</div>)}
        </Alert>
      )}

      {/* ── BUILDER TAB ── */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* LEFT: Config panels */}
          <Box sx={{ flex: '0 0 56%', minWidth: 0 }}>

            {/* Templates */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('sb.quickStart', lang)}</SectionTitle>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  ['generic',   tr('fb.tpl.generic', lang)],
                  ['interview', tr('fb.tpl.interview', lang)],
                  ['socio',     tr('fb.tpl.socio', lang)],
                ].map(([key, label]) => (
                  <Button key={key} variant="outlined" size="small" onClick={() => applyTemplate(key)}
                    sx={{ borderColor: '#E2E8F0', color: '#475569', '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' } }}>
                    {label}
                  </Button>
                ))}
              </Box>
            </Paper>

            {/* Form Definition meta */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('fb.definition', lang)}</SectionTitle>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label={tr('fb.name', lang)} size="small" value={form.name} onChange={e => updateMeta({ name: e.target.value })} sx={halfSx} placeholder={tr('fb.egName', lang)} />
                <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
                  <InputLabel>{tr('fb.type', lang)}</InputLabel>
                  <Select value={form.formType} label={tr('fb.type', lang)} onChange={e => handleFormTypeChange(e.target.value as FormType)}>
                    <MenuItem value="generic">{tr('formType.generic', lang)}</MenuItem>
                    <MenuItem value="interview">{tr('formType.interview', lang)}</MenuItem>
                    <MenuItem value="socio">{tr('formType.socio', lang)}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  label={tr('form.weightInFinal', lang)}
                  size="small"
                  type="number"
                  value={form.weightInFinalScore}
                  onChange={e => updateMeta({ weightInFinalScore: Number(e.target.value) })}
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  sx={{ flex: 1, mb: 2, minWidth: 160 }}
                />
                <FormControlLabel control={<Switch checked={form.isMandatoryForClosure} onChange={e => updateMeta({ isMandatoryForClosure: e.target.checked })} size="small" />} label={tr('fb.mandatory', lang)} sx={{ mb: 2 }} />
                <FormControlLabel control={<Switch checked={form.isActive} onChange={e => updateMeta({ isActive: e.target.checked })} size="small" />} label={tr('common.active', lang)} sx={{ mb: 2 }} />
              </Box>

              <FormLabel sx={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 600, display: 'block', mb: 0.75 }}>{tr('fb.roles', lang)}</FormLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {ALL_ROLES.map(role => (
                  <Chip
                    key={role}
                    label={translateOr(`role.${role}`, lang, role)}
                    size="small"
                    onClick={() => toggleRole(role)}
                    color={form.roleRelevancy.includes(role) ? 'primary' : 'default'}
                    variant={form.roleRelevancy.includes(role) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Paper>

            {/* Structure / display */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('fb.structure', lang)}</SectionTitle>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label={tr('fb.icon', lang)} size="small" value={form.structure.icon} onChange={e => updateStructMeta({ icon: e.target.value })} sx={{ width: 110, mb: 2 }} />
                <TextField label={tr('fb.displayTitle', lang)} size="small" value={form.structure.title} onChange={e => updateStructMeta({ title: e.target.value })} sx={{ flex: 1, mb: 2 }} placeholder={tr('fb.displayTitleHint', lang)} />
              </Box>
              <TextField label={tr('fb.description', lang)} size="small" value={form.structure.description} onChange={e => updateStructMeta({ description: e.target.value })} sx={fullSx} fullWidth placeholder={tr('fb.descriptionHint', lang)} />
              {form.formType !== 'socio' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, columnGap: 2 }}>
                  {structureFlags.map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={!!(form.structure as any)[key]}
                          onChange={e => updateStructMeta({ [key]: e.target.checked })}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.8rem' }}>{label}</Typography>}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Form fields / Socio boxes */}
            {form.formType !== 'socio' ? (
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <SectionTitle>{tr('fb.fields', lang)} ({form.structure.structure.length})</SectionTitle>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<GradeIcon sx={{ fontSize: 13 }} />} onClick={() => addField('gradebox')}
                      sx={{ fontSize: '0.72rem', color: '#6366F1', borderColor: '#6366F1', '&:hover': { bgcolor: '#EEF2FF' } }}>{tr('fb.field.gradebox', lang)}</Button>
                    <Button size="small" variant="outlined" startIcon={<TextFieldsIcon sx={{ fontSize: 13 }} />} onClick={() => addField('textboxblock')}
                      sx={{ fontSize: '0.72rem', color: '#10B981', borderColor: '#10B981', '&:hover': { bgcolor: '#F0FDF4' } }}>{tr('fb.field.textboxblock', lang)}</Button>
                    <Button size="small" variant="outlined" startIcon={<FlagIcon sx={{ fontSize: 13 }} />} onClick={() => addField('flagblock')}
                      sx={{ fontSize: '0.72rem', color: '#F59E0B', borderColor: '#F59E0B', '&:hover': { bgcolor: '#FFFBEB' } }}>{tr('fb.flag', lang)}</Button>
                    <Button size="small" variant="outlined" startIcon={<ListIcon sx={{ fontSize: 13 }} />} onClick={() => addField('selectbox')}
                      sx={{ fontSize: '0.72rem', color: '#8B5CF6', borderColor: '#8B5CF6', '&:hover': { bgcolor: '#F5F3FF' } }}>{tr('fb.field.selectbox', lang)}</Button>
                  </Box>
                </Box>
                {form.structure.structure.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5, border: '2px dashed #E2E8F0', borderRadius: 2, color: '#94A3B8' }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>{tr('fb.noFields', lang)}</Typography>
                  </Box>
                ) : (
                  form.structure.structure.map((field, i) => (
                    <FieldBlock
                      key={i}
                      index={i}
                      total={form.structure.structure.length}
                      field={field}
                      options={bankParams}
                      lang={lang}
                      onChange={f => updateField(i, f)}
                      onRemove={() => removeField(i)}
                      onMove={dir => moveField(i, dir)}
                    />
                  ))
                )}
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <SectionTitle>{tr('fb.socioBoxes', lang)} ({(form.structure.boxes ?? []).length})</SectionTitle>
                  <Button size="small" variant="outlined" startIcon={<AddSmallIcon sx={{ fontSize: 13 }} />} onClick={addSocioBox}
                    sx={{ fontSize: '0.72rem', color: '#0EA5E9', borderColor: '#0EA5E9', '&:hover': { bgcolor: '#F0F9FF' } }}>{tr('fb.addBox', lang)}</Button>
                </Box>
                {(form.structure.boxes ?? []).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5, border: '2px dashed #E2E8F0', borderRadius: 2, color: '#94A3B8' }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>{tr('fb.noBoxes', lang)}</Typography>
                  </Box>
                ) : (
                  (form.structure.boxes ?? []).map((box, i) => (
                    <SocioBoxBlock
                      key={box.id}
                      index={i}
                      total={(form.structure.boxes ?? []).length}
                      box={box}
                      onChange={b => updateSocioBox(i, b)}
                      onRemove={() => removeSocioBox(i)}
                      onMove={dir => moveSocioBox(i, dir)}
                    />
                  ))
                )}
              </Paper>
            )}
          </Box>

          {/* RIGHT: JSON preview */}
          <Box sx={{ flex: '0 0 44%', position: 'sticky', top: 24, minWidth: 0 }}>
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden', maxHeight: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#0F172A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{tr('sb.jsonOutput', lang)}</Typography>
                <Typography sx={{ color: '#475569', fontSize: '0.68rem' }}>
                  {jsonStr.split('\n').length} {tr('sb.lines', lang)}
                </Typography>
              </Box>
              <Box
                component="pre"
                dir="ltr"
                sx={{
                  m: 0, p: 2.5,
                  overflow: 'auto',
                  flex: 1,
                  bgcolor: '#0F172A',
                  color: '#E2E8F0',
                  fontSize: '0.72rem',
                  fontFamily: '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
                  lineHeight: 1.65,
                  '&::-webkit-scrollbar': { width: 6, height: 6 },
                  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#334155', borderRadius: 3 },
                }}
              >
                {jsonStr}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {/* ── FORMS BANK TAB ── */}
      {activeTab === 1 && (
        <Box>
          {bankLoading ? (
            <Typography sx={{ color: '#94A3B8', py: 4, textAlign: 'center' }}>{tr('fb.loadingBank', lang)}</Typography>
          ) : bankForms.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
              <Typography sx={{ fontSize: '0.95rem', mb: 1 }}>{tr('fb.noBank', lang)}</Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>{tr('sb.noBankHint', lang)}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {bankForms.map(bf => (
                <Paper
                  key={bf.id}
                  elevation={0}
                  sx={{
                    border: '1px solid #E2E8F0',
                    borderLeft: `3px solid ${FORM_TYPE_COLOR[bf.formType] ?? '#94A3B8'}`,
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: editingFormId === bf.id ? '#F8FAFF' : 'white',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>{bf.name}</Typography>
                      <Chip
                        label={translateOr(`formType.${bf.formType}`, lang, bf.formType)}
                        size="small"
                        sx={{
                          bgcolor: FORM_TYPE_COLOR[bf.formType] ?? '#94A3B8',
                          color: '#FFF',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 18,
                        }}
                      />
                      {!bf.isActive && <Chip label={tr('common.inactive', lang)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', color: '#94A3B8', borderColor: '#CBD5E1' }} />}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {bf.roleRelevancy.map(r => (
                        <Chip key={r} label={translateOr(`role.${r}`, lang, r)} size="small" variant="outlined" sx={{ height: 17, fontSize: '0.63rem', color: '#475569' }} />
                      ))}
                      <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94A3B8', alignSelf: 'center', ml: 0.5 }}>
                        weight set per cycle
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant={editingFormId === bf.id ? 'contained' : 'outlined'}
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleEditBankForm(bf)}
                      sx={editingFormId === bf.id
                        ? { bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' }, fontSize: '0.75rem' }
                        : { fontSize: '0.75rem' }
                      }
                    >
                      {editingFormId === bf.id ? tr('sb.editing', lang) : tr('common.edit', lang)}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setDeleteTarget(bf)}
                      disabled={deleteMutation.isPending}
                      sx={{ fontSize: '0.75rem' }}
                    >{tr('common.delete', lang)}</Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('fb.deleteTitle', lang)}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {tr('sb.deleteBodyBefore', lang)} <strong>{deleteTarget?.name}</strong> {tr('sb.deleteBodyAfter', lang)}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{tr('common.cancel', lang)}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >{tr('common.delete', lang)}</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default FormBuilder;
